/* Motion progressively enhances visible content. It never gates layout or shopping. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const enabled = () => !reduced.matches && document.body.dataset.apexMotion !== 'false';
  const updateCounters = (root = document) => {
    root.querySelectorAll('.input__slider').forEach(input => {
      const counter = input.closest('.product-info__quantity-selector')?.querySelector('[data-input-counter]');
      if (!counter) return;
      const progress = Math.max(0,Math.min(100,Number(input.value) / (Number(input.max) || 1) * 100));
      input.style.background = `linear-gradient(to right,rgb(var(--ul-button-background-secondary)) ${progress}%,rgb(var(--ul-border-color)) ${progress}%)`;
      counter.textContent = input.value;
    });
  };
  window.updateCounter = () => updateCounters();
  function mountReviews(panel, signal) {
    const scroller = panel.querySelector('.apex-review-scroll');
    const track = scroller?.firstElementChild;
    if (!track || panel.dataset.autoScroll !== 'true') return;
    const originals = [...track.children];
    const toggle = panel.querySelector('[data-review-toggle]');
    const toggleText = toggle?.querySelector('[data-review-toggle-text]');
    if (originals.length < 2) return;
    const mobile = window.matchMedia('(max-width: 749px)');
    let distance = 0, position = scroller.scrollLeft, frame = 0, previous = 0;
    let visible = false, hovered = false, focused = false, held = false, paused = false;
    let manual = false, resumeTimer, resizeFrame;
    const stop = () => { cancelAnimationFrame(frame); frame = 0; previous = 0; };
    const canRun = () => enabled() && visible && !document.hidden && distance > 0 && !hovered && !focused && !held && !paused && !manual;
    const tick = time => {
      if (!canRun()) { stop(); return; }
      if (previous) {
        const duration = Math.max(10, Number(mobile.matches ? panel.dataset.speedMobile : panel.dataset.speed) || 35);
        position = (position + Math.min(time - previous, 64) * distance / (duration * 1000)) % distance;
        scroller.scrollLeft = position;
      }
      previous = time;
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (!canRun() || frame) return;
      position = scroller.scrollLeft % distance;
      previous = 0;
      frame = requestAnimationFrame(tick);
    };
    const settle = () => {
      clearTimeout(resumeTimer);
      if (held) return;
      resumeTimer = setTimeout(() => { manual = false; start(); }, 1800);
    };
    const interact = () => { manual = true; stop(); settle(); };
    const rebuild = () => {
      stop();
      const oldDistance = distance;
      const oldPosition = oldDistance ? scroller.scrollLeft % oldDistance : scroller.scrollLeft;
      track.querySelectorAll('[data-review-clone]').forEach(clone => clone.remove());
      delete scroller.dataset.apexLoop;
      distance = 0;
      if (enabled()) {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        distance = originals.reduce((total, card) => total + card.getBoundingClientRect().width + gap, 0);
        if (distance > 0) {
          const copies = Math.ceil((scroller.clientWidth + gap) / distance);
          for (let i = 0; i < copies; i++) originals.forEach(card => {
            const clone = card.cloneNode(true);
            clone.dataset.reviewClone = '';
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('inert', '');
            [clone, ...clone.querySelectorAll('[id]')].forEach(node => node.removeAttribute('id'));
            track.append(clone);
          });
          scroller.dataset.apexLoop = 'true';
          position = oldDistance ? oldPosition / oldDistance * distance : oldPosition % distance;
          scroller.scrollLeft = position;
        }
      }
      if (toggle) toggle.hidden = !enabled() || distance <= 0;
      start();
    };
    scroller.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = true; stop();
    }, {signal});
    scroller.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = false; start();
    }, {signal});
    scroller.addEventListener('focusin', () => {
      focused = document.activeElement?.matches(':focus-visible');
      if (focused) stop();
    }, {signal});
    scroller.addEventListener('focusout', () => {
      queueMicrotask(() => {
        focused = scroller.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        start();
      });
    }, {signal});
    scroller.addEventListener('keydown', () => { focused = true; stop(); }, {signal});
    scroller.addEventListener('pointerdown', () => { held = true; interact(); }, {passive:true,signal});
    const release = () => { if (held) { held = false; settle(); } };
    window.addEventListener('pointerup', release, {passive:true,signal});
    window.addEventListener('pointercancel', release, {passive:true,signal});
    scroller.addEventListener('wheel', interact, {passive:true,signal});
    scroller.addEventListener('scroll', () => { if (manual) settle(); }, {passive:true,signal});
    toggle?.addEventListener('click', () => {
      paused = !paused;
      toggle.dataset.paused = String(paused);
      if (toggleText) toggleText.textContent = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
      if (paused) stop(); else start();
    }, {signal});
    document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else start(); }, {signal});
    const reviewObserver = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      if (visible) start(); else stop();
    }, {threshold:0.05});
    reviewObserver.observe(scroller);
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(rebuild);
    });
    resizeObserver.observe(scroller);
    mobile.addEventListener('change', rebuild, {signal});
    reduced.addEventListener('change', rebuild, {signal});
    rebuild();
    signal.addEventListener('abort', () => {
      stop(); clearTimeout(resumeTimer); cancelAnimationFrame(resizeFrame);
      reviewObserver.disconnect(); resizeObserver.disconnect();
      track.querySelectorAll('[data-review-clone]').forEach(clone => clone.remove());
      delete scroller.dataset.apexLoop;
      if (toggle) toggle.hidden = true;
    }, {once:true});
  }
  function mount(root) {
    updateCounters(root);
    const controller = new AbortController();
    const { signal } = controller;
    root.addEventListener('input', event => {if (event.target.matches('.input__slider')) updateCounters(root);},{signal});
    const animations = new Set();
    const nodes = [...root.querySelectorAll('[data-apex-reveal]')];
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({target,isIntersecting}) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        if (!enabled() || window.Shopify?.designMode) return;
        const frames = target.classList.contains('media-with-text__media')
          ? [{opacity:.5,clipPath:'inset(4% 4% 4% 4%)'},{opacity:1,clipPath:'inset(0% 0% 0% 0%)'}]
          : [{opacity:0,translate:'0 24px'},{opacity:1,translate:'0 0'}];
        const animation = target.animate(frames, {duration:700,easing:'cubic-bezier(.2,.65,.2,1)',fill:'none'});
        animations.add(animation);
        animation.finished.then(() => animations.delete(animation)).catch(() => {});
      });
    }, {threshold:.04,rootMargin:'0px 0px -12px 0px'});
    nodes.forEach(node => observer.observe(node));
    const hero = root.querySelector('[data-apex-hero]');
    if (hero) {
      const img = hero.querySelector('img');
      if (img && enabled() && hero.dataset.entrance === 'true') {
        // Scale only the photograph. Copy remains in its final position from first paint.
        const animation = img.animate([{scale:'1.025'},{scale:'1'}],{duration:1400,easing:'cubic-bezier(.2,.7,.3,1)'});
        animations.add(animation);
        animation.finished.then(() => animations.delete(animation)).catch(() => {});
      }
      if (img && hero.dataset.parallax === 'true') {
        let queued = false;
        const update = () => {
          queued = false;
          if (!enabled()) {img.style.transform = '';return;}
          const box = hero.getBoundingClientRect();
          if (box.bottom < 0 || box.top > innerHeight) return;
          const progress = Math.max(0,Math.min(1,-box.top / box.height));
          img.style.transform = `scale(${1 + progress * .035})`;
        };
        window.addEventListener('scroll',() => {
          if (!queued) {queued = true;requestAnimationFrame(update);}
        },{passive:true,signal});
        reduced.addEventListener('change',update,{signal});
        update();
      }
    }
    root.querySelectorAll('[data-apex-testimonials]').forEach(panel => mountReviews(panel, signal));
    const cleanup = () => {observer.disconnect();controller.abort();animations.forEach(a=>a.cancel());};
    instances.set(root,cleanup);
  }
  const start = () => document.querySelectorAll('.shopify-section').forEach(section => mount(section));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  document.addEventListener('shopify:section:load',event => {
    instances.get(event.target)?.();
    mount(event.target);
  });
  document.addEventListener('shopify:section:unload',event => {
    instances.get(event.target)?.();instances.delete(event.target);
  });
  reduced.addEventListener('change',() => {
    if (reduced.matches) document.getAnimations().forEach(animation => animation.cancel());
  });
})();
